import { InfiniteData, useInfiniteQuery } from '@tanstack/react-query'
import { useCallback, useMemo, useState } from 'react'
import { useDispatch } from 'react-redux'
import { CreditCardDropdownInfiniteScrollProps } from 'src/components/CreditCardDropdown/CreditCardDropdown'
import { CreditCardListModel } from 'src/models/creditCard.models'
import { CreditCardsResponse } from 'src/queries/creditCard.queries'
import {
  CreditCardVariant,
  virtualCardFormCreditCardsQueryKeys,
} from 'src/queries/queries.constants'
import { createGetCreditCardSuccessAction } from 'src/redux/actions/data/creditCards.actions'

import { fetchCreditCardsForForm } from '../network/virtualCardFormCreditCards.network'

export type VirtualCardFormScreen =
  | 'Standard'
  | 'BillPay'
  | 'MultipleBillPay'
  | 'Multiple'
  | 'Recurring'
  | 'GuestAccess'
  | 'RecurringGuest'
  | 'DecliningBalance'
  | 'IssuerCycle'

export const screenToVariant: Record<VirtualCardFormScreen, CreditCardVariant> = {
  Standard: 'standard',
  BillPay: 'standard',
  MultipleBillPay: 'standard',
  Multiple: 'standard',
  Recurring: 'recurring',
  GuestAccess: 'guestAccess',
  RecurringGuest: 'guestAccess',
  DecliningBalance: 'balanceLock',
  IssuerCycle: 'issuerCycle',
}

export interface CreditCardDropdownData {
  options: CreditCardListModel[]
  infiniteScrollProps: CreditCardDropdownInfiniteScrollProps
  isLoading: boolean
}

export interface UseVirtualCardFormCreditCardsResult {
  standard: CreditCardDropdownData
  recurring: CreditCardDropdownData
  guestAccess: CreditCardDropdownData
  balanceLock: CreditCardDropdownData
  issuerCycle: CreditCardDropdownData
  autoSelectedCreditCard: CreditCardListModel | undefined
}

function useVariantQuery(
  variant: CreditCardVariant,
  enabled: boolean,
  billPayEnabled?: boolean,
) {
  const dispatch = useDispatch()
  const [keyword, setKeyword] = useState<string | undefined>(undefined)

  const query = useInfiniteQuery<
    CreditCardsResponse,
    Error,
    InfiniteData<CreditCardsResponse, number>,
    ReturnType<typeof virtualCardFormCreditCardsQueryKeys.list>,
    number
  >({
    queryKey: virtualCardFormCreditCardsQueryKeys.list(variant, keyword),
    queryFn: async ({ pageParam }) => {
      const data = await fetchCreditCardsForForm(
        variant,
        pageParam,
        keyword,
        billPayEnabled,
      )
      // Mirror into the legacy creditCards.listResponses slice so form
      // consumers that still read the selected card from Redux
      // (VirtualCardForm, CreditCardAndAmountInputBox) resolve it. Remove
      // once those consumers read the card from React Query / props.
      data.creditCards.forEach((creditCard) => {
        dispatch(createGetCreditCardSuccessAction({ creditCard }))
      })
      return data
    },
    enabled,
    initialPageParam: 0,
    getNextPageParam: (lastPage) => {
      const { pagination } = lastPage
      if (pagination.page < pagination.numberOfPages - 1) {
        return pagination.page + 1
      }
      return undefined
    },
    gcTime: 0,
  })

  const options = useMemo(
    () =>
      query.data?.pages.flatMap((page) =>
        page.creditCards.map((cc) => new CreditCardListModel(cc)),
      ) ?? [],
    [query.data],
  )

  const currentPage =
    query.data !== undefined && query.data.pages.length > 0
      ? query.data.pages.length - 1
      : 0

  const fetchNextPage = query.fetchNextPage

  const fetch = useCallback(
    (filterInput?: string, page?: number) => {
      setKeyword(filterInput)
      if (page !== undefined && page > 0) {
        fetchNextPage()
      }
    },
    [fetchNextPage],
  )

  const infiniteScrollProps: CreditCardDropdownInfiniteScrollProps = {
    fetch,
    page: currentPage,
    loading: query.isFetching,
    complete: !query.hasNextPage,
  }

  return {
    options,
    infiniteScrollProps,
    isLoading: query.isLoading,
  }
}

export function useVirtualCardFormCreditCards(
  screen: VirtualCardFormScreen,
  options?: { billPayEnabled?: boolean; enabled?: boolean },
): UseVirtualCardFormCreditCardsResult {
  const activeVariant = screenToVariant[screen]
  const hookEnabled = options?.enabled ?? true

  const standardQuery = useVariantQuery(
    'standard',
    hookEnabled,
    options?.billPayEnabled,
  )
  const recurringQuery = useVariantQuery('recurring', hookEnabled)
  const guestAccessQuery = useVariantQuery('guestAccess', hookEnabled)
  const balanceLockQuery = useVariantQuery('balanceLock', hookEnabled)
  const issuerCycleQuery = useVariantQuery('issuerCycle', hookEnabled)

  const activeQueryData = useMemo(() => {
    switch (activeVariant) {
      case 'recurring':
        return recurringQuery.options
      case 'guestAccess':
        return guestAccessQuery.options
      case 'balanceLock':
        return balanceLockQuery.options
      case 'issuerCycle':
        return issuerCycleQuery.options
      case 'standard':
      default:
        return standardQuery.options
    }
  }, [
    activeVariant,
    standardQuery.options,
    recurringQuery.options,
    guestAccessQuery.options,
    balanceLockQuery.options,
    issuerCycleQuery.options,
  ])

  const autoSelectedCreditCard = useMemo(() => {
    if (activeQueryData && activeQueryData.length === 1) {
      return activeQueryData[0]
    }
    return undefined
  }, [activeQueryData])

  return {
    standard: {
      options: standardQuery.options,
      infiniteScrollProps: standardQuery.infiniteScrollProps,
      isLoading: standardQuery.isLoading,
    },
    recurring: {
      options: recurringQuery.options,
      infiniteScrollProps: recurringQuery.infiniteScrollProps,
      isLoading: recurringQuery.isLoading,
    },
    guestAccess: {
      options: guestAccessQuery.options,
      infiniteScrollProps: guestAccessQuery.infiniteScrollProps,
      isLoading: guestAccessQuery.isLoading,
    },
    balanceLock: {
      options: balanceLockQuery.options,
      infiniteScrollProps: balanceLockQuery.infiniteScrollProps,
      isLoading: balanceLockQuery.isLoading,
    },
    issuerCycle: {
      options: issuerCycleQuery.options,
      infiniteScrollProps: issuerCycleQuery.infiniteScrollProps,
      isLoading: issuerCycleQuery.isLoading,
    },
    autoSelectedCreditCard,
  }
}
